import { useQuery } from '@tanstack/react-query'
import type { StandardOption } from '@/api/types'
import { standardsApi } from '@/api/clients/standardsApi'
import { TextInput } from '@/components/common/ui'

// standardKey is the canonical framework value stored — first alias, else lowercased
// name. Matches the backend compliance resolver's key set so deadlines resolve.
// eslint-disable-next-line react-refresh/only-export-components
export function standardKey(s: StandardOption): string {
  return s.aliases?.[0] ?? s.name.toLowerCase()
}

function tooltip(s: StandardOption): string {
  const parts: string[] = []
  if (s.version) parts.push(s.version)
  if (s.effective_from)
    parts.push(`valid from ${s.effective_from}${s.effective_to ? ` to ${s.effective_to}` : ''}`)
  if (s.migration_deadline) parts.push(`migrate by ${s.migration_deadline}`)
  if (s.aliases?.length) parts.push(`aliases: ${s.aliases.join(', ')}`)
  const meta = parts.join(' · ')
  return s.description ? `${s.description}\n${meta}` : meta
}

function csv(v: string): string[] {
  return v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

// StandardsPicker is a chip multi-select of discovery compliance standards. Stored values
// are canonical alias keys. Falls back to a free-text CSV input when discovery is off / no
// standards are available, so editing stays usable.
export function StandardsPicker({
  orgId,
  value,
  onChange,
}: {
  orgId: string
  value: string[]
  onChange: (next: string[]) => void
}) {
  const standards = useQuery({
    queryKey: ['standards', orgId],
    queryFn: () => standardsApi.list(orgId),
    enabled: !!orgId,
  })

  if (!standards.data || standards.data.length === 0) {
    return (
      <TextInput
        defaultValue={value.join(', ')}
        placeholder="DORA, NIST_800-53"
        onChange={(e) => onChange(csv(e.target.value))}
      />
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {standards.data.map((s) => {
        const key = standardKey(s)
        const selected = value.includes(key)
        const year = s.migration_deadline?.slice(0, 4)
        return (
          <button
            key={key}
            type="button"
            title={tooltip(s)}
            onClick={() =>
              onChange(selected ? value.filter((k) => k !== key) : [...value, key])
            }
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              selected
                ? 'bg-primary text-white border-primary dark:bg-secondary dark:border-secondary'
                : 'border-border-light dark:border-border-dark text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            {s.name}
            {year ? ` · ${year}` : ''}
          </button>
        )
      })}
    </div>
  )
}
